import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
  style?: React.CSSProperties;
  /** Largeur minimum du dropdown (px). Permet d'élargir la liste quand le trigger
   *  est étroit (ex: pill de statut "Qualifié" qui ne laisserait pas voir
   *  proprement des labels comme "Ne répond pas"). */
  dropdownMinWidth?: number;
}

const SelectField: React.FC<SelectFieldProps> = ({ value, onChange, options, className = '', style, dropdownMinWidth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(o => o.value === value) ?? options[0];

  useEffect(() => {
    if (!isOpen) return;
    if (triggerRef.current) {
      setDropdownRect(triggerRef.current.getBoundingClientRect());
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        listRef.current && !listRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`orion-lead-select flex items-center justify-between gap-2 ${className}`}
        style={style}
      >
        <span className="truncate">{selectedOption?.label}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && dropdownRect && createPortal(
        <div
          ref={listRef}
          className="orion-custom-dropdown"
          style={dropdownMinWidth && dropdownMinWidth > dropdownRect.width
            ? {
                position: 'fixed',
                top: `${dropdownRect.bottom + 4}px`,
                left: `${dropdownRect.left}px`,
                width: `${dropdownMinWidth}px`,
                zIndex: 9999,
              }
            : {
                position: 'fixed',
                top: `${dropdownRect.bottom + 4}px`,
                left: `${dropdownRect.left}px`,
                width: `${dropdownRect.width}px`,
                zIndex: 9999,
              }}
        >
          {options.map(option => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`orion-custom-dropdown-item ${option.value === value ? 'orion-custom-dropdown-item-active' : ''}`}
            >
              {option.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default SelectField;
